import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PdfPreparedFor, tw } from '~/components/pdfs'
import {
  type Company,
  type Listing,
  type ListingBroker,
  type Organization,
  type ReportPdfOptions,
  type User,
} from '~/models'
import { cn, fmtNumberAbbr } from '~/utils'
import {
  CompChart,
  CompStats,
  CompTable,
  PdfBrokerFooter,
  type CompChartConfig,
  type CompStatsConfig,
  type CompTableConfig,
} from './components'
import { PdfDisclaimersPage, ReportPdfTitlePage } from './pages'
import { getSaleListingsData, getSaleListingsStatsData } from './utils'

// Sale Comp Chart Configuration Function
const getSaleChartConfig = (isLandReport: boolean): CompChartConfig => {
  return {
    bars: [
      {
        dataKey: 'salePricePerSf',
        fill: '#a78bfa',
        name: 'Listed Price/SF',
      },
    ],
    yAxisWidth: 60,
    yAxisFormatter: (value: number) => `$${value}/SF`,
  }
}

const getSaleStatsConfig = (isLandReport: boolean): CompStatsConfig[] => {
  const statsConfig: CompStatsConfig[] = [
    {
      key: 'averageSalePrice',
      label: 'Avg. Listed Price',
      formatter: (value: number) => `$${Math.round(value).toLocaleString()}`,
    },
  ]

  const landReportConfig: CompStatsConfig[] = [
    {
      key: 'averageSalePricePerAcre',
      label: 'Avg. Listed $/Ac',
      formatter: (value: number) => `$${Math.round(value).toLocaleString()}/ac`,
    },
    {
      key: 'averageSalePricePerSf',
      label: 'Avg. Listed $/SF',
      formatter: (value: number) => `$${Math.round(value).toLocaleString()}/SF`,
    },
    {
      key: 'averageAcres',
      label: 'Avg. Lot Size',
      formatter: (value: number) =>
        `${
          value > 100 ? Math.round(value).toLocaleString() : value.toFixed(2)
        } ac`,
      secondaryFormatter: (value: number) =>
        `${fmtNumberAbbr(value * 43560)} SF`,
    },
  ]

  const buildingReportConfig: CompStatsConfig[] = [
    {
      key: 'averageSalePricePerSf',
      label: 'Avg. Listed $/SF',
      formatter: (value: number) => `$${Math.round(value).toLocaleString()}/SF`,
    },
    {
      key: 'averageBuildingSf',
      label: 'Avg. Building SF',
      formatter: (value: number) => `${Math.round(value).toLocaleString()} SF`,
    },
  ]

  return [
    ...statsConfig,
    ...(isLandReport ? landReportConfig : buildingReportConfig),
  ]
}

// Sale Comp Table Configuration Function
const getSaleCompTableConfig = (isLandReport: boolean): CompTableConfig => {
  const baseColumns = [
    {
      key: 'address',
      label: 'Address',
    },
  ]

  const landReportColumns = [
    {
      key: 'salePrice',
      label: 'Listed Price',
      formatter: (value?: number) =>
        value ? `$${value.toLocaleString()}` : '-',
    },
    {
      key: 'salePricePerAcre',
      label: 'L. Price/Ac',
      formatter: (value?: number) =>
        value ? `$${fmtNumberAbbr(value)}/ac` : '-',
    },
    {
      key: 'salePricePerSf',
      label: 'L. Price/SF',
      formatter: (value?: number) =>
        value ? `$${value.toLocaleString()}/SF` : '-',
    },
    {
      key: 'acres',
      label: 'Acres',
      formatter: (value?: number) => value?.toLocaleString() || '-',
    },
    {
      key: 'lotSizeSf',
      label: 'Lot Size SF',
      formatter: (value?: number) =>
        value ? `${value.toLocaleString()} SF` : '-',
    },
  ]

  const buildingReportColumns = [
    {
      key: 'salePrice',
      label: 'Listed Price',
      formatter: (value: number) =>
        value ? `$${value.toLocaleString()}` : '-',
    },
    {
      key: 'salePricePerSf',
      label: 'L. Price/SF',
      formatter: (value: number) =>
        value ? `$${value.toLocaleString()}/SF` : '-',
    },
    {
      key: 'buildingSf',
      label: 'Building SF',
      formatter: (value: number) =>
        value ? `${value.toLocaleString()} SF` : '-',
    },
  ]

  const endColumns = [
    {
      key: 'date',
      label: 'Date',
    },
  ]

  return {
    columns: [
      ...baseColumns,
      ...(isLandReport ? landReportColumns : buildingReportColumns),
      ...endColumns,
    ],
  }
}

export default function ReportSaleCompPdf({
  organization,
  title,
  user,
  company,
  brokers,
  listings,
  pdfOptions,
}: {
  organization: Organization
  title: string
  user: User
  company?: Company
  brokers?: ListingBroker[]
  listings: Listing[]
  pdfOptions: ReportPdfOptions
}) {
  // Filter for sale and saleLease listings
  const saleListings = listings.filter(
    (listing) => listing.type === 'sale' || listing.type === 'saleLease'
  )

  // Determine if this is a land report
  const isLandReport =
    saleListings.filter((listing) => listing.propertyTypes[0] === 'Land')
      .length >=
    saleListings.length - Math.round(saleListings.length / 4)

  // Get processed sale data
  const saleData = getSaleListingsData(listings, isLandReport)
  const saleStatsData = getSaleListingsStatsData(saleData)

  return (
    <Document title={title}>
      {pdfOptions.showTitlePage && (
        <ReportPdfTitlePage
          title={title}
          company={company}
          user={user}
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {saleData.length > 0 && (
        <Page
          size="LETTER"
          style={tw('flex flex-col gap-4 px-8 pb-24 pt-8 font-sans text-sm')}
          orientation="portrait">
          <View
            style={tw(
              cn(
                !pdfOptions.showTitlePage &&
                  'flex flex-row justify-between gap-6'
              )
            )}>
            {!pdfOptions.showTitlePage && (
              <View style={tw('mb-3')}>
                <Text style={tw('font-heading mb-2 text-2xl leading-tight')}>
                  {title}
                </Text>
                <PdfPreparedFor />
              </View>
            )}

            <View style={tw(cn(!pdfOptions.showTitlePage && 'text-right'))}>
              <Text style={tw('text-xl font-bold')}>
                {saleData.length} Sale Listings
              </Text>
            </View>
          </View>

          <CompChart
            data={saleData}
            config={getSaleChartConfig(isLandReport)}
          />

          <CompStats
            statsData={saleStatsData}
            config={getSaleStatsConfig(isLandReport)}
          />

          <CompTable
            data={saleData}
            config={getSaleCompTableConfig(isLandReport)}
          />

          <PdfBrokerFooter
            organization={organization}
            user={user}
            company={company}
            brokers={brokers}
          />
        </Page>
      )}
      <PdfDisclaimersPage
        pdfOptions={pdfOptions}
        organization={organization}
        orientation="portrait"
      />
    </Document>
  )
}
